import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import type {
  AnalyticsLiveKpis,
  AnalyticsSnapshotResponse,
  AnalyticsTab,
  AnalyticsTimeRange,
} from "@/features/admin/adminAnalytics.types";
import { AdminClientError } from "@/features/admin/api/adminClient";

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new AdminClientError("missing-config", "Supabase environment variables are not configured.");
  }
}

function normalizeFunctionError(error: unknown, fallbackCode: string) {
  if (!error || typeof error !== "object") {
    return new AdminClientError(fallbackCode);
  }

  const candidate = error as {
    context?: {
      json?: {
        code?: string;
        message?: string;
      };
    };
    message?: string;
  };

  return new AdminClientError(
    candidate.context?.json?.code ?? fallbackCode,
    candidate.context?.json?.message ?? candidate.message,
    error,
  );
}

async function getFunctionAuthHeaders() {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error || !session?.access_token) {
    throw new AdminClientError(
      "ADMIN_FORBIDDEN",
      error?.message ?? "No authenticated admin session available.",
      error,
    );
  }

  return {
    Authorization: `Bearer ${session.access_token}`,
  };
}

export async function loadAnalyticsLiveKpis(): Promise<AnalyticsLiveKpis> {
  ensureConfigured();
  const headers = await getFunctionAuthHeaders();

  const { data, error } = await supabase.functions.invoke("get-analytics-live", {
    body: {},
    headers,
  });

  if (error) {
    throw normalizeFunctionError(error, "ANALYTICS_LOAD_FAILED");
  }

  return data as AnalyticsLiveKpis;
}

export async function loadAnalyticsSnapshot(
  tab: AnalyticsTab,
  range: Pick<AnalyticsTimeRange, "endDate" | "startDate">,
): Promise<AnalyticsSnapshotResponse> {
  ensureConfigured();
  const headers = await getFunctionAuthHeaders();

  const { data, error } = await supabase.functions.invoke("get-analytics-snapshot", {
    body: {
      endDate: range.endDate,
      startDate: range.startDate,
      tab,
    },
    headers,
  });

  if (error) {
    throw normalizeFunctionError(error, "ANALYTICS_LOAD_FAILED");
  }

  return data as AnalyticsSnapshotResponse;
}

export async function updateAnalyticsSnapshot(targetDate?: string): Promise<{
  generatedAt: string;
  metricRows: number;
  targetDate: string;
}> {
  ensureConfigured();
  const headers = await getFunctionAuthHeaders();

  const { data, error } = await supabase.functions.invoke("update-analytics-snapshot", {
    body: {
      targetDate,
    },
    headers,
  });

  if (error) {
    throw normalizeFunctionError(error, "ANALYTICS_LOAD_FAILED");
  }

  return data as {
    generatedAt: string;
    metricRows: number;
    targetDate: string;
  };
}

export async function exportAnalyticsCsv(
  tab: AnalyticsTab,
  range: Pick<AnalyticsTimeRange, "endDate" | "startDate">,
): Promise<Blob> {
  ensureConfigured();
  const headers = await getFunctionAuthHeaders();

  const { data, error } = await supabase.functions.invoke("export-analytics-csv", {
    body: {
      endDate: range.endDate,
      startDate: range.startDate,
      tab,
    },
    headers,
  });

  if (error) {
    throw normalizeFunctionError(error, "ANALYTICS_EXPORT_FAILED");
  }

  return data instanceof Blob ? data : new Blob([String(data ?? "")], { type: "text/csv" });
}
